import { useCallback, useMemo } from "react";
import { useCookies } from "react-cookie";

import {
  Address,
  MeQuery_me_Member_merchants_Merchant_addresses_Address as MerchantAddress,
} from "@/Apollo/schema";
import { useAuth } from "@/auth";
import useMerchant from "@/hooks/useMerchant";
import { showModal } from "@/ui/Modal2";

/** Shared with the PDP delivery block; predates WHO-5226, keep the name. */
const COOKIE_NAME = "__whoppah_delivery_address";
const GUEST_ADDRESS_ID = "GUEST";

/**
 * WHO-5226. The one delivery location for the whole site: header, mobile menu
 * and the PDP shipping block all read it here, so a change in any of them is
 * visible in the others straight away (react-cookie re-renders every consumer).
 *
 * A logged-in merchant's saved address book always wins over the cookie; the
 * cookie then only remembers *which* saved address is active. That also covers
 * the address being deleted elsewhere - the id no longer resolves and we fall
 * back to the active (first) saved address instead of showing a stale one.
 */
export const useDeliveryLocation = () => {
  const [cookies, setCookie, removeCookie] = useCookies([COOKIE_NAME]);
  const cookieAddress = cookies[COOKIE_NAME] as Address | undefined;

  const { isLoggedIn, isReady } = useAuth();
  const isRegisteredUser = isLoggedIn && isReady;

  const { id: merchantId, addresses } = useMerchant();

  const address = useMemo<MerchantAddress | Address | undefined>(() => {
    if (addresses.length > 0) {
      return (
        addresses.find(saved => saved.id === cookieAddress?.id) ?? addresses[0]
      );
    }

    /** No address book: only a guest cookie is ours to trust. */
    return cookieAddress?.id === GUEST_ADDRESS_ID ? cookieAddress : undefined;
  }, [addresses, cookieAddress]);

  const saveAddress = useCallback(
    (next: Address) => {
      setCookie(COOKIE_NAME, next);
    },
    [setCookie]
  );

  const clearAddress = useCallback(() => {
    removeCookie(COOKIE_NAME);
  }, [removeCookie]);

  const openAddAddressModal = useCallback(async () => {
    const added = await showModal("add-edit-address", {
      merchantId,
      /** Guests have no address book, so the modal edits the cookie in place. */
      address: isRegisteredUser ? undefined : address,
    });

    if (!added) {
      return;
    }

    saveAddress(isRegisteredUser ? added : { id: GUEST_ADDRESS_ID, ...added });
  }, [address, isRegisteredUser, merchantId, saveAddress]);

  /**
   * The add/edit modal when there is nothing to pick from, the change-address
   * modal (select, edit, or add another) once the merchant has an address book.
   */
  const openAddressModal = useCallback(async () => {
    if (addresses.length === 0) {
      await openAddAddressModal();
      return;
    }

    const response = await showModal("change-address", {
      merchantId,
      addresses,
      selectedAddress: address,
      hasAddNew: true,
    });

    if (response?.addNew) {
      await openAddAddressModal();
      return;
    }

    const selected = addresses.find(saved => saved.id === response?.id);

    if (selected) {
      saveAddress(selected);
    }
  }, [address, addresses, merchantId, openAddAddressModal, saveAddress]);

  return {
    address,
    addresses,
    isRegisteredUser,
    merchantId,
    saveAddress,
    clearAddress,
    openAddressModal,
  };
};
